import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Copy, RefreshCw, Target } from "lucide-react";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/adminAuth";

/* 📈 AD TRACKER (admin) — which ?src= link brings visitors that actually
   sign up, connect a REAL account and run a bot. Funnel truth, no vanity. */
const PLATFORMS = ["tg_ads", "tiktok", "telegram", "push", "ig", "yt", "other"];

const AdTrackerCard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState("tg_ads");
  const [country, setCountry] = useState("ng");

  const load = () => {
    setLoading(true);
    api.get("/admin/adtrack/stats", { headers: authHeader() })
      .then((r) => setRows(r.data.rows || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const tag = `${platform}_${country.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "")}`.slice(0, 32);
  const link = `${window.location.origin}/?src=${tag}`;  const copyLink = () => {
    navigator.clipboard?.writeText(link)
      .then(() => toast.success("Ad link copied — paste it as the ad's destination URL"))
      .catch(() => toast.error("Could not copy — select and copy manually"));
  };

  // 📣 Telegram Ads rollup — every source tagged tg_ads*
  const tg = rows.filter((r) => r.src.startsWith("tg_ads"));
  const tgSum = (k) => tg.reduce((a, r) => a + (r[k] || 0), 0);

  return (
    <div data-testid="ad-tracker" className="mt-6 rounded-xl border border-vts-line bg-vts-panel p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-vts-blue" />
        <h3 className="text-sm font-bold text-white font-mono">📈 AD TRACKER</h3>
        <span className="text-[9px] font-mono text-vts-silver/50">per-ad funnel: visits → signups → real accounts → running bots</span>
        <button data-testid="adtrack-refresh" onClick={load} className="ml-auto p-1.5 rounded-md hover:bg-vts-surface transition">
          <RefreshCw className={`w-3.5 h-3.5 text-vts-silver/55 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* 📣 Telegram Ads traffic — live rollup of every tg_ads* source */}
      <div data-testid="adtrack-tgads-strip" className="rounded-lg border border-[#2962FF]/40 bg-[#2962FF]/10 px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-black font-mono text-[#7aa2ff] uppercase tracking-wide">📣 Telegram Ads traffic</span>
          <span className="text-[8px] font-mono text-vts-silver/45">all tg_ads* links combined</span>
        </div>
        {tg.length === 0 ? (
          <p data-testid="adtrack-tgads-empty" className="text-[10px] font-mono text-vts-silver/60">
            No visitors from Telegram Ads yet. Use the builder below (platform <b className="text-white">tg_ads</b>) and set that link as your ad's destination — the moment someone taps the ad, this lights up.
          </p>
        ) : (
          <div className="grid grid-cols-5 gap-2 text-center">
            {[["Today", tgSum("visits_today")], ["Visits", tgSum("visits")], ["Signups", tgSum("signups")], ["Real accts", tgSum("real_accounts")], ["Trading", tgSum("trading")]].map(([l, v]) => (
              <div key={l}>
                <p data-testid={`adtrack-tgads-${l.toLowerCase().replace(/\s/g, "-")}`} className={`text-base font-black font-mono ${v > 0 ? "text-white" : "text-vts-silver/40"}`}>{v}</p>
                <p className="text-[8px] font-mono uppercase text-vts-silver/50">{l}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* link builder */}
      <div className="flex items-end gap-2 flex-wrap">
        <label className="block">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Platform</span>
          <select data-testid="adtrack-platform" value={platform} onChange={(e) => setPlatform(e.target.value)}
            className="block h-9 rounded-md border border-vts-line bg-vts-surface px-2 text-xs font-mono text-white">
            {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Country code</span>
          <input data-testid="adtrack-country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="ng"
            className="block w-20 h-9 rounded-md border border-vts-line bg-vts-surface px-2 text-xs font-mono text-white" />
        </label>
        <div className="flex-1 min-w-[220px]">
          <span className="text-[9px] font-mono uppercase text-vts-silver/50">Your trackable ad link</span>
          <div className="flex items-center gap-1.5 h-9 rounded-md border border-vts-blue/40 bg-vts-surface px-2">
            <Link2 className="w-3.5 h-3.5 text-vts-blue shrink-0" />
            <span data-testid="adtrack-link" className="text-xs font-mono text-white truncate">{link}</span>
            <button data-testid="adtrack-copy" onClick={copyLink} className="ml-auto p-1 rounded hover:bg-vts-line transition" title="Copy">
              <Copy className="w-3.5 h-3.5 text-vts-blue" />
            </button>
          </div>
        </div>
      </div>

      {/* funnel table */}
      <div className="overflow-x-auto">
        <table className="w-full text-[11px] font-mono">
          <thead>
            <tr className="text-left text-[9px] uppercase text-vts-silver/50 border-b border-vts-line">
              <th className="py-1.5 pr-2">Source</th>
              <th className="py-1.5 pr-2 text-right">Today</th>
              <th className="py-1.5 pr-2 text-right">Visits</th>
              <th className="py-1.5 pr-2 text-right">Signups</th>
              <th className="py-1.5 pr-2 text-right">Real accounts</th>
              <th className="py-1.5 text-right">Running bots 🏆</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-center text-vts-silver/40 text-[10px]">
                No ad traffic yet — share a ?src= link above and this table fills itself.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.src} data-testid={`adtrack-row-${r.src}`} className="border-b border-vts-line/40">
                <td className="py-1.5 pr-2 text-white font-bold">{r.src}</td>
                <td className={`py-1.5 pr-2 text-right ${r.visits_today > 0 ? "text-vts-profit font-bold" : "text-vts-silver/50"}`}>{r.visits_today || 0}</td>
                <td className="py-1.5 pr-2 text-right text-vts-silver/80">{r.visits}</td>
                <td className="py-1.5 pr-2 text-right text-vts-silver/80">{r.signups}</td>
                <td className="py-1.5 pr-2 text-right text-white">{r.real_accounts}</td>
                <td className={`py-1.5 text-right font-bold ${r.trading > 0 ? "text-vts-profit" : "text-vts-silver/50"}`}>{r.trading}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[9px] font-mono text-vts-silver/40">
        Scale the source with the cheapest "running bots" — that's where commission comes from. Visits and likes are vanity.
      </p>
    </div>
  );
};

export default AdTrackerCard;
